import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Tabs,
  TabList,
  Tab,
  Badge,
  Select,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { exportToCSV } from '@/lib/utils/csvUtils'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'

type TabValue = 'all' | 'trash'

export default function FieldEngineersPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        full_name: {
          name: 'Full Name',
          withSort: true,
          maxWidth: '200px',
        },
        user: {
          name: 'User',
          withSort: false,
          maxWidth: '200px',
          render: (value: any) => {
            if (!value || !value.user_login) return '-'
            return `${value.user_login} (${value.user_email})`
          },
        },
        cluster: {
          name: 'Cluster',
          withSort: true,
          maxWidth: '150px',
        },
        province: {
          name: 'Province',
          withSort: true,
          maxWidth: '150px',
        },
        region: {
          name: 'Region',
          withSort: true,
          maxWidth: '100px',
        },
        launch_point: {
          name: 'Launch Point',
          withSort: true,
          maxWidth: '200px',
        },
        updated_at: {
          name: 'Updated At',
          withSort: true,
          maxWidth: '150px',
        },
      },
      dataSource: activeTab === 'trash' ? '/organization/field-engineers?trash=1' : '/organization/field-engineers',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No field engineers found',
      otherActions:
        activeTab === 'all'
          ? [
              {
                name: 'View Activity',
                onClick: (row: { user_id?: number }) => {
                  if (row.user_id) {
                    navigate(`/access-control/user-activity/${row.user_id}`)
                  }
                },
                show: (row: { user_id?: number }) => !!row.user_id,
              },
            ]
          : [],
    }),
    [activeTab, navigate]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) {
      searchRef.current.value = ''
    }
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const query = searchRef.current?.value || ''
    setSearchQuery(query)
    tableRef.current?.applyFilter('search', query)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }

    if (bulkAction === 'delete' && activeTab === 'trash') {
      setModalMessage('You are about to permanently delete these items from your site. This action cannot be undone.')
    } else {
      setModalMessage('Are you sure to apply this change?')
    }
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      let url = ''
      let payload: any = { ids: selectedIds }
      let successMessage = 'Action completed successfully'

      if (bulkAction === 'restore') {
        url = '/organization/field-engineers/bulk/restore'
        successMessage = 'Field Engineers restored successfully'
      } else if (bulkAction === 'delete') {
        url = activeTab === 'trash' ? '/organization/field-engineers/bulk/force-delete' : '/organization/field-engineers/bulk/delete'
        successMessage =
          activeTab === 'trash' ? 'Field Engineers permanently deleted successfully' : 'Field Engineers moved to trash successfully'
      }

      await apiClient.post(url, payload)
      showSuccess('Success', successMessage)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (error: any) {
      showError(error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiClient.get('/organization/field-engineers/template/download', { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = 'field_engineers_template.csv'
      a.click()
      URL.revokeObjectURL(url)
      showSuccess('Success', 'Template downloaded')
    } catch (e: any) {
      showError(e)
    }
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsImporting(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await apiClient.post<{ data: { imported: number; errors: string[] } }>(
        '/organization/field-engineers/import',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      const d = data?.data ?? { imported: 0, errors: [] }
      showSuccess('Success', `Imported ${d.imported} field engineer(s)`)
      if (d.errors?.length) {
        showWarning(d.errors.slice(0, 3).join('; ') + (d.errors.length > 3 ? '...' : ''))
      }
      tableRef.current?.reload()
    } catch (err: any) {
      showError(err)
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  const handleExportData = async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: '/organization/field-engineers',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: [
          'ID',
          'User ID',
          'Full Name',
          'Cluster',
          'Province',
          'Region',
          'Launch Point',
          'Address',
          'Longitude',
          'Latitude',
          'Launch Longitude',
          'Launch Latitude',
          'Comments',
          'Created At',
          'Updated At',
          'Deleted At',
        ],
        dataMapper: (fe: any) => [
          fe.id || '',
          fe.user_id || '',
          fe.full_name || '',
          fe.cluster || '',
          fe.province || '',
          fe.region || '',
          fe.launch_point || '',
          fe.address || '',
          fe.longitude || '',
          fe.latitude || '',
          fe.launch_longitude || '',
          fe.launch_latitude || '',
          fe.comments || '',
          fe.created_at || '',
          fe.updated_at || '',
          fe.deleted_at || '',
        ],
        filename: `field-engineers_export_${activeTab === 'trash' ? 'trash' : 'all'}_${new Date().toISOString().split('T')[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      })
    } catch (error: any) {
      showError(error)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Field Engineers
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage Field Engineers and their certifications
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>
                        All{' '}
                        <Badge ml={2} colorScheme="blue">
                          {totalRows}
                        </Badge>
                      </Tab>
                      {totalTrash > 0 && (
                        <Tab>
                          Trash{' '}
                          <Badge ml={2} colorScheme="red">
                            {totalTrash}
                          </Badge>
                        </Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/organization/field-engineers"
                      onExport={handleExportData}
                      onDownloadTemplate={handleDownloadTemplate}
                      createLabel="Add Field Engineer"
                      isExporting={isExporting}
                      isImporting={isImporting}
                      fileInputRef={fileInputRef}
                      onFileUpload={handleFileUpload}
                      acceptFileTypes=".csv"
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search field engineers..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody
              p={0}
              pt={1}
              pb={4}
              mx={5}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
              position="relative"
            >
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/organization/field-engineers/${id}`)}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
